import React, { useState, useRef } from "react";
import toast from "react-hot-toast";
import { FiX, FiUpload, FiInfo, FiFileText } from "react-icons/fi";
import { importLeads } from "../../../services/crmApi";

const ALLOWED_EXTENSIONS = [".xlsx", ".xls"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const validateImportFile = (f) => {
  if (!f) return "Please select a file to import";
  const name = (f.name || "").toLowerCase();
  const hasValidExt = ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
  if (!hasValidExt) {
    return "Unsupported file type. Please upload an Excel file (.xlsx or .xls).";
  }
  if (f.size === 0) {
    return "The selected file is empty.";
  }
  if (f.size > MAX_FILE_SIZE) {
    return "File is too large. Maximum size is 10 MB.";
  }
  return null;
};

// Build a useful success message from whatever the backend reports. Falls back
// to a generic message so we never render "undefined imported".
const buildImportSummary = (res) => {
  if (res?.message) return res.message;
  const data = res?.data || {};
  const imported = data.imported ?? data.created ?? data.successCount;
  const skipped = data.skipped ?? data.failed ?? data.errorCount;
  if (typeof imported === "number") {
    let msg = `${imported} lead${imported === 1 ? "" : "s"} imported`;
    if (typeof skipped === "number" && skipped > 0) {
      msg += `, ${skipped} skipped`;
    }
    return msg;
  }
  return "Leads imported successfully";
};

const ImportLeadsModal = ({ boardId, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    // Reset the input so re-selecting the same (corrected) file still fires.
    if (e.target) e.target.value = "";
    if (!f) return;
    const validationError = validateImportFile(f);
    if (validationError) {
      setFile(null);
      toast.error(validationError);
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    const validationError = validateImportFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    try {
      setSubmitting(true);
      const res = await importLeads(boardId, file);
      toast.success(buildImportSummary(res));
      onImported?.();
      onClose?.();
    } catch (err) {
      // toast handled by axios interceptor
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full shadow-xl flex flex-col">
        <div className="flex justify-between items-start p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Import Leads
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Upload an Excel file to import leads into this board
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FiX size={22} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded-md p-3 flex gap-2">
            <FiInfo
              size={16}
              className="flex-shrink-0 mt-0.5 text-blue-600"
            />
            <div className="space-y-1">
              <p className="font-medium">Use the exported template</p>
              <p className="text-xs leading-relaxed">
                For best results, export the board first and use that file as
                your template. The importer maps columns by header
                (case-insensitive) and recognizes:
              </p>
              <p className="text-xs">
                <span className="font-semibold">Name, Email, Phone,
                Address, Expected Deal Revenue, Lead Source, Lead Source
                Other, Category, Status, Priority, Tags, Stage, Order, Student
                Email, Assigned Email, Sales Person Email, Activity History,
                Notes</span>
              </p>
              <ul className="text-xs list-disc ml-5 space-y-0.5 mt-1">
                <li>
                  <span className="font-semibold">Tags</span> use{" "}
                  <code className="bg-blue-100 px-1 rounded">;</code> as a
                  separator.
                </li>
                <li>
                  <span className="font-semibold">Stage</span> is matched by
                  column name (falls back to first stage).
                </li>
                <li>
                  <span className="font-semibold">Activity History</span>{" "}
                  (JSON) and <span className="font-semibold">Notes</span>{" "}
                  (separated by{" "}
                  <code className="bg-blue-100 px-1 rounded">|||</code>) are
                  preserved if present.
                </li>
                <li>
                  Sales Person uploads will only restore their own data.
                </li>
              </ul>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Choose file <span className="text-red-500">*</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
              id="import-leads-file"
            />
            <label
              htmlFor="import-leads-file"
              className="flex items-center justify-between gap-3 px-4 py-3 border-2 border-dashed border-gray-300 hover:border-blue-400 rounded-md cursor-pointer transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FiFileText size={18} className="text-gray-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 truncate">
                  {file ? file.name : "Click to select an Excel file"}
                </span>
              </div>
              <span className="text-xs text-blue-600 font-medium flex-shrink-0">
                {file ? "Change" : "Browse"}
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Supported formats: .xlsx, .xls
            </p>
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!file || submitting}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <FiUpload size={16} />
            {submitting ? "Importing..." : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportLeadsModal;
